import React, { useState, useEffect } from 'react'
import { IonIcon, IonButton } from '@ionic/react'
import { sparklesOutline, closeOutline, settingsOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/authStore'
import { UserService } from '../services/userService'
import { useTheme } from '../contexts/ThemeContext'

interface MotivationalWelcomeHeaderProps {
  className?: string
  additionalInfo?: string
}

const MotivationalWelcomeHeader: React.FC<MotivationalWelcomeHeaderProps> = ({ 
  className = '', 
  additionalInfo = ''
}) => {
  const { t } = useTranslation()
  const { user, isCoach } = useAuthStore()
  const { currentTheme } = useTheme()
  const [currentMessage, setCurrentMessage] = useState('')
  const [isVisible, setIsVisible] = useState(true)
  const [isDismissedForToday, setIsDismissedForToday] = useState(false)

  // Motivational messages for coaches
  const coachMessages = [
    t('motivational.coach.message1'),
    t('motivational.coach.message2'), 
    t('motivational.coach.message3'),
    t('motivational.coach.message4'),
    t('motivational.coach.message5')
  ]

  // Motivational messages for coachees
  const coacheeMessages = [
    t('motivational.coachee.message1'),
    t('motivational.coachee.message2'),
    t('motivational.coachee.message3'),
    t('motivational.coachee.message4'),
    t('motivational.coachee.message5')
  ]

  useEffect(() => {
    if (!user?.show_motivational_messages) {
      setIsVisible(false)
      return
    }

    // Check if dismissed for today
    const dismissedDate = localStorage.getItem('motivational_dismissed_date')
    const today = new Date().toDateString()
    
    if (dismissedDate === today) {
      setIsDismissedForToday(true)
      return
    }

    // Get daily message based on date
    const messages = isCoach() ? coachMessages : coacheeMessages
    const today_seed = new Date().getDate()
    const messageIndex = today_seed % messages.length
    setCurrentMessage(messages[messageIndex])
  }, [user, isCoach, coachMessages, coacheeMessages])

  const handleDismissForToday = () => {
    const today = new Date().toDateString()
    localStorage.setItem('motivational_dismissed_date', today)
    setIsDismissedForToday(true)
  }

  const handleTurnOffMessages = async () => {
    try {
      await UserService.updateProfile(user!.id, {
        show_motivational_messages: false
      })
      setIsVisible(false)
    } catch (error) {
      console.error('Error turning off motivational messages:', error)
    }
  }

  // Create theme-aware gradient colors
  const getGradientColors = () => {
    if (!currentTheme) {
      return 'from-primary-500 to-primary-600'
    }

    const primary = currentTheme.colors.primary
    const primaryLight = currentTheme.colors.primaryLight
    const secondary = currentTheme.colors.secondary

    // For coaches, use primary colors
    if (isCoach()) {
      return {
        background: `linear-gradient(135deg, ${primary} 0%, ${primaryLight} 100%)`,
        textColor: currentTheme.colors.onPrimary
      }
    } else {
      // For coachees, use secondary colors for distinction
      return {
        background: `linear-gradient(135deg, ${secondary} 0%, ${primary} 100%)`,
        textColor: currentTheme.colors.onSecondary
      }
    }
  }

  const gradientStyle = getGradientColors()

  if (!isVisible || !user?.show_motivational_messages || isDismissedForToday) {
    // Show simple welcome header without motivational message
    return (
      <div 
        className={`motivational-welcome-header mb-6 mx-2 sm:mx-0 p-4 rounded-xl relative ${className}`}
        style={{
          background: typeof gradientStyle === 'string' ? undefined : gradientStyle.background,
          color: typeof gradientStyle === 'string' ? '#ffffff' : gradientStyle.textColor,
        }}
      >
        <h1 className="text-2xl font-heading font-bold mb-2">
          {isCoach() 
            ? `Welcome back, Coach ${user?.full_name?.split(' ')[0] || 'Coach'}! 👋`
            : `Welcome back, ${user?.full_name?.split(' ')[0] || 'Friend'}! 🌟`
          }
        </h1>
        {additionalInfo && (
          <p style={{ opacity: 0.9 }}>
            {additionalInfo}
          </p>
        )}
      </div>
    )
  }

  return (
    <div 
      className={`motivational-welcome-header mb-6 mx-2 sm:mx-0 p-4 rounded-xl relative ${className}`}
      style={{
        background: typeof gradientStyle === 'string' ? undefined : gradientStyle.background,
        color: typeof gradientStyle === 'string' ? '#ffffff' : gradientStyle.textColor,
      }}
    >
      {/* Main Welcome Content */}
      <div className="mb-3">
        <h1 className="text-2xl font-heading font-bold mb-2">
          {isCoach() 
            ? `Welcome back, Coach ${user?.full_name?.split(' ')[0] || 'Coach'}! 👋`
            : `Welcome back, ${user?.full_name?.split(' ')[0] || 'Friend'}! 🌟`
          }
        </h1>
        {additionalInfo && (
          <p style={{ opacity: 0.9 }}>
            {additionalInfo}
          </p>
        )}
      </div>

      {/* Motivational Message Section */}
      <div 
        className="motivational-overlay backdrop-blur-sm rounded-lg p-3 border"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.15)',
          borderColor: 'rgba(255, 255, 255, 0.2)',
        }}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">
            <IonIcon 
              icon={sparklesOutline} 
              className="text-yellow-300 text-lg"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-relaxed">
              {currentMessage}
            </p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div 
          className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 mt-3 pt-2 border-t"
          style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }}
        >
          <IonButton 
            fill="clear" 
            size="small"
            onClick={handleDismissForToday}
            className="text-xs w-full sm:w-auto"
            style={{ 
              '--color': 'rgba(255, 255, 255, 0.8)',
              '--color-hover': 'rgba(255, 255, 255, 1)',
            }}
          >
            {t('motivational.dismissForToday')}
          </IonButton>
          <IonButton 
            fill="clear" 
            size="small"
            onClick={handleTurnOffMessages}
            className="text-xs w-full sm:w-auto"
            style={{ 
              '--color': 'rgba(255, 255, 255, 0.8)',
              '--color-hover': 'rgba(255, 255, 255, 1)',
            }}
          >
            <IonIcon icon={settingsOutline} className="mr-1" />
            {t('motivational.turnOff')}
          </IonButton>
        </div>
      </div>
    </div>
  )
}

export default MotivationalWelcomeHeader 